import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';
import { LogLevel } from '../../../types/dex';

interface SwapTesterProps {
  tokenAddress: string;
}

/**
 * 代币交换测试组件
 * 允许用户在DEX上执行代币交换操作
 */
const SwapTester: React.FC<SwapTesterProps> = ({ tokenAddress }) => {
  const { dexService, config, isInitialized, addLog } = useDexService();
  const [selectedWallet, setSelectedWallet] = useState('');
  const [toToken, setToToken] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fromTokenInfo, setFromTokenInfo] = useState<{
    symbol: string;
    decimals: number;
    balance: string;
  } | null>(null);
  const [toTokenInfo, setToTokenInfo] = useState<{
    symbol: string;
    decimals: number;
  } | null>(null);
  const [estimatedOutput, setEstimatedOutput] = useState<string | null>(null);
  const [priceImpact, setPriceImpact] = useState<string | null>(null);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [approving, setApproving] = useState(false);

  // 当tokenAddress或selectedWallet变化时获取代币信息
  useEffect(() => {
    if (tokenAddress && selectedWallet) {
      fetchFromTokenInfo();
    }
  }, [tokenAddress, selectedWallet]);

  // 获取输入代币信息
  const fetchFromTokenInfo = async () => {
    if (!tokenAddress || !ethers.utils.isAddress(tokenAddress) || !selectedWallet) {
      return;
    }

    try {
      const tokenInfo = await dexService?.getTokenInfo(tokenAddress);
      const balance = await dexService?.checkTokenBalance(tokenAddress, selectedWallet);
      if (tokenInfo && balance) {
        setFromTokenInfo({
          symbol: tokenInfo.symbol,
          decimals: tokenInfo.decimals,
          balance: ethers.utils.formatUnits(balance, tokenInfo.decimals)
        });
      }
    } catch (err: any) {
      console.error('获取代币信息时出错:', err);
      addLog({
        level: LogLevel.ERROR,
        message: `获取代币信息失败: ${err.message || '未知错误'}`
      });
    }
  };

  // 处理钱包选择变更
  const handleWalletChange = (e: any) => {
    setSelectedWallet(e.target.value);
    setFromTokenInfo(null);
    setToTokenInfo(null);
    setEstimatedOutput(null);
    setPriceImpact(null);
    setError(null);
    setSuccess(null);
  };

  // 处理输出代币地址变更
  const handleToTokenChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    setToToken(address);
    setToTokenInfo(null);
    setEstimatedOutput(null);
    setPriceImpact(null);
    setError(null);
    setSuccess(null);

    if (address && ethers.utils.isAddress(address)) {
      try {
        const tokenInfo = await dexService?.getTokenInfo(address);
        if (tokenInfo) {
          setToTokenInfo({
            symbol: tokenInfo.symbol,
            decimals: tokenInfo.decimals
          });
        }
      } catch (err: any) {
        console.error('获取代币信息时出错:', err);
        addLog({
          level: LogLevel.ERROR,
          message: `获取代币信息失败: ${err.message || '未知错误'}`
        });
      }
    }
  };

  // 处理金额变更
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    setEstimatedOutput(null);
    setPriceImpact(null);
    setError(null);
    setSuccess(null);
  };

  // 使用最大余额
  const handleUseMaxBalance = () => {
    if (fromTokenInfo) {
      setAmount(fromTokenInfo.balance);
    }
  };

  // 交换代币位置
  const handleSwapTokens = () => {
    setToToken(tokenAddress);
    setToTokenInfo(fromTokenInfo);
    setFromTokenInfo({
      ...toTokenInfo,
      balance: '0' // 重置余额，因为需要重新查询
    } as typeof fromTokenInfo);
    setAmount('');
    setEstimatedOutput(null);
    setPriceImpact(null);
  };

  // 获取交换预估
  const getSwapEstimate = async () => {
    if (!isInitialized || !dexService) {
      setError('DexService未初始化，请先配置服务');
      addLog({
        level: LogLevel.ERROR,
        message: 'DexService未初始化，无法获取交换预估'
      });
      return;
    }

    if (!tokenAddress || !toToken || !amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      return;
    }

    try {
      setLoading(true);
      const amountIn = ethers.utils.parseUnits(amount, fromTokenInfo?.decimals || 18);
      
      // 获取预估输出金额
      const estimation = await dexService?.getAmountOut({
        tokenIn: tokenAddress,
        tokenOut: toToken,
        amountIn: amountIn.toString(),
        tokenInDecimals: fromTokenInfo?.decimals || 18,
        tokenOutDecimals: toTokenInfo?.decimals || 18,
        slippage: 0.5 // 默认滑点0.5%
      });
      
      // 格式化输出金额
      const formattedOutput = ethers.utils.formatUnits(
        estimation.amountOut,
        toTokenInfo?.decimals || 18
      );
      
      setEstimatedOutput(formattedOutput);
      setPriceImpact(estimation.priceImpact.toFixed(2));
      
      addLog({
        level: LogLevel.INFO,
        message: `预估交换: ${amount} ${fromTokenInfo?.symbol} ≈ ${formattedOutput} ${toTokenInfo?.symbol}`,
        data: { priceImpact: estimation.priceImpact }
      });

      // 检查是否需要授权
      const allowance = await dexService?.getAllowance(
        tokenAddress,
        selectedWallet,
        config?.dex?.router?.address || ''
      );
      
      setNeedsApproval(allowance.lt(amountIn));
    } catch (err: any) {
      console.error('获取交换预估时出错:', err);
      setError(`获取交换预估时出错: ${err.message || '未知错误'}`);
      addLog({
        level: LogLevel.ERROR,
        message: `获取交换预估失败: ${err.message || '未知错误'}`
      });
    } finally {
      setLoading(false);
    }
  };

  // 检查代币授权
  const checkTokenAllowance = async () => {
    if (!isInitialized || !dexService || !config || !selectedWallet || !tokenAddress || !amount) {
      return;
    }

    try {
      const amountIn = ethers.utils.parseUnits(amount, fromTokenInfo?.decimals || 18);
      const allowance = await dexService.getAllowance(
        tokenAddress,
        selectedWallet,
        config.dex.router.address
      );

      setNeedsApproval(allowance.lt(amountIn));
    } catch (err: any) {
      console.error('检查代币授权时出错:', err);
      addLog({
        level: LogLevel.ERROR,
        message: `检查代币授权失败: ${err.message || '未知错误'}`
      });
    }
  };

  // 授权代币
  const approveToken = async () => {
    if (!isInitialized || !dexService || !config || !selectedWallet || !tokenAddress || !amount) {
      return;
    }

    try {
      setApproving(true);
      setError(null);

      const amountIn = ethers.utils.parseUnits(amount, fromTokenInfo?.decimals || 18);
      const tx = await dexService.approve(
        tokenAddress,
        config.dex.router.address,
        ethers.BigNumber.from(ethers.constants.MaxUint256), // 转换为BigNumber类型
        selectedWallet
      );

      addLog({
        level: LogLevel.INFO,
        message: '代币授权交易已发送',
        data: { txHash: tx.hash }
      });

      await tx.wait();
      
      addLog({
        level: LogLevel.SUCCESS,
        message: '代币授权成功',
        data: { txHash: tx.hash }
      });

      setNeedsApproval(false);
    } catch (err: any) {
      console.error('授权代币时出错:', err);
      setError(`授权代币时出错: ${err.message || '未知错误'}`);
      addLog({
        level: LogLevel.ERROR,
        message: `授权代币失败: ${err.message || '未知错误'}`
      });
    } finally {
      setApproving(false);
    }
  };

  // 执行代币交换
  const executeSwap = async () => {
    if (!isInitialized || !dexService || !config || !selectedWallet || !tokenAddress || !toToken || !amount) {
      setError('请确保所有必要信息已填写');
      addLog({
        level: LogLevel.WARNING,
        message: '缺少执行交换所需的信息'
      });
      return;
    }

    if (isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('请输入有效的金额');
      addLog({
        level: LogLevel.WARNING,
        message: '输入金额无效'
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // 检查代币余额
      const balance = await dexService.checkTokenBalance(tokenAddress, selectedWallet);
      
      const amountIn = ethers.utils.parseUnits(amount, fromTokenInfo?.decimals || 18);
      
      if (balance.lt(amountIn)) {
        throw new Error(`余额不足，当前余额: ${ethers.utils.formatUnits(balance, fromTokenInfo?.decimals || 18)} ${fromTokenInfo?.symbol}`);
      }

      // 执行交换
      const result = await dexService.swap({
        tokenIn: tokenAddress,
        tokenOut: toToken,
        amountIn: amount,
        tokenInDecimals: fromTokenInfo?.decimals || 18,
        tokenOutDecimals: toTokenInfo?.decimals || 18,
        slippage: 0.5 // 默认滑点0.5%
      }, selectedWallet);

      if (result.success) {
        const formattedAmountOut = result.amountOut ? 
          ethers.utils.formatUnits(result.amountOut, toTokenInfo?.decimals || 18) :
          '未知数量';

        setSuccess(`交换成功! 交易哈希: ${result.txHash}`);
        addLog({
          level: LogLevel.SUCCESS,
          message: `交换成功: ${amount} ${fromTokenInfo?.symbol} => ${formattedAmountOut} ${toTokenInfo?.symbol}`,
          data: {
            txHash: result.txHash,
            gasUsed: result.gasUsed?.toString(),
            effectiveGasPrice: result.effectiveGasPrice?.toString()
          }
        });

        // 清空输入
        setAmount('');
        setEstimatedOutput(null);
        setPriceImpact(null);

        // 更新余额
        const newBalance = await dexService.checkTokenBalance(tokenAddress, selectedWallet);
        if (fromTokenInfo) {
          setFromTokenInfo({
            ...fromTokenInfo,
            balance: ethers.utils.formatUnits(newBalance, fromTokenInfo.decimals)
          });
        }
      } else {
        setError(`交换失败: ${result.error || '未知错误'}`);
        addLog({
          level: LogLevel.ERROR,
          message: `交换失败: ${result.error || '未知错误'}`
        });
      }
    } catch (err: any) {
      console.error('执行交换时出错:', err);
      setError(`执行交换时出错: ${err.message || '未知错误'}`);
      addLog({
        level: LogLevel.ERROR,
        message: `执行交换时出错: ${err.message || '未知错误'}`
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理复制哈希
  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash).then(
      () => {
        addLog({
          level: LogLevel.INFO,
          message: '交易哈希已复制到剪贴板'
        });
      },
      (err) => {
        console.error('复制失败:', err);
      }
    );
  };

  // 在输入值变化时更新预估和检查授权
  useEffect(() => {
    if (tokenAddress && toToken && amount && !isNaN(Number(amount)) && Number(amount) > 0) {
      getSwapEstimate();
      checkTokenAllowance();
    }
  }, [tokenAddress, toToken, amount, selectedWallet]);

  if (!isInitialized || !config) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Alert severity="warning">
          DexService未初始化，请先配置服务
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        代币交换测试
      </Typography>

      {/* 钱包选择 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>选择钱包</InputLabel>
          <Select
            value={selectedWallet}
            label="选择钱包"
            onChange={handleWalletChange}
          >
            {config.wallets.map((wallet, index) => (
              <MenuItem 
                key={wallet.address || `wallet-${index}`} 
                value={wallet.address || ''}
              >
                钱包 {index + 1}: {(wallet.address || '').slice(0, 6)}...{(wallet.address || '').slice(-4)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 代币输入输出 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            输入代币地址：{tokenAddress}
          </Typography>
          {fromTokenInfo && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" display="block">
                {fromTokenInfo.symbol} (小数位: {fromTokenInfo.decimals})
              </Typography>
              <Typography variant="caption" display="block">
                余额: {fromTokenInfo.balance}
                <Button 
                  size="small" 
                  onClick={handleUseMaxBalance}
                  sx={{ ml: 1, minWidth: 'auto', p: 0 }}
                >
                  最大
                </Button>
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={handleSwapTokens} color="primary">
            <SwapHorizIcon />
          </IconButton>
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            label="输出代币地址"
            variant="outlined"
            fullWidth
            value={toToken}
            onChange={handleToTokenChange}
            placeholder="输入ERC20代币合约地址"
            size="small"
          />
          {toTokenInfo && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {toTokenInfo.symbol} (小数位: {toTokenInfo.decimals})
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="交换金额"
            variant="outlined"
            fullWidth
            value={amount}
            onChange={handleAmountChange}
            placeholder="输入要交换的代币数量"
            size="small"
            type="number"
            InputProps={{
              inputProps: { min: 0, step: 'any' }
            }}
          />
        </Grid>
      </Grid>

      {/* 交换预估 */}
      {estimatedOutput && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              预计获得
            </Typography>
            <Typography variant="h6">
              {estimatedOutput} {toTokenInfo?.symbol}
            </Typography>
            {priceImpact && (
              <Typography 
                variant="body2" 
                color={Number(priceImpact) > 5 ? 'error' : 'text.secondary'}
              >
                价格影响: {priceImpact}%
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 成功提示 */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
          <IconButton
            size="small"
            onClick={() => handleCopyHash(success.split(': ')[1])}
            sx={{ ml: 1 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      {/* 操作按钮 */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {needsApproval ? (
          <Button
            variant="contained"
            color="primary"
            onClick={approveToken}
            disabled={loading || approving || !selectedWallet || !tokenAddress || !toToken || !amount}
            fullWidth
          >
            {approving ? <CircularProgress size={24} /> : '授权代币'}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={executeSwap}
            disabled={loading || !selectedWallet || !tokenAddress || !toToken || !amount}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : '执行交换'}
          </Button>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        提示：执行交换前请确保已经授权DEX路由器使用您的代币。您可以在"代币授权"页面中进行授权操作。
      </Alert>
    </Paper>
  );
};

export default SwapTester;
